import type * as RadixPopover from '@radix-ui/react-popover';
import type { PropsWithChildren } from 'react';

import { cn } from '~/chords/lib/cn';
import { Popover } from '~/chords/Popover';

import { IconButton } from '../IconButton';
import type { IconButtonProps } from '../IconButton/IconButton';
import { KebabMenuItem } from './components/KebabMenuItem';

type IconButtonSize = NonNullable<IconButtonProps['size']>;
type IconButtonVariant = NonNullable<IconButtonProps['iconVariant']>;

export interface KebabMenuPopoverOptions {
  side?: RadixPopover.PopoverContentProps['side'];
  align?: RadixPopover.PopoverContentProps['align'];
}

export type KebabMenuProps = PropsWithChildren<{
  size?: IconButtonSize;
  menuOptions?: KebabMenuPopoverOptions;
  disabled?: boolean;
  iconVariant?: IconButtonVariant;
  /**
   * Callback fired when focus moves into the menu content after it opens.
   * Call `event.preventDefault()` to prevent the default focus behavior
   * (e.g. to keep focus on the trigger).
   */
  onOpenAutoFocus?: RadixPopover.PopoverContentProps['onOpenAutoFocus'];
}>;

export function KebabMenuComponent({
  children,
  size = 'large',
  menuOptions,
  disabled = false,
  iconVariant = 'solid',
  onOpenAutoFocus,
}: KebabMenuProps) {
  return (
    <Popover
      content={
        <div
          className={cn(
            'py-xs',
            'flex flex-col overflow-hidden bg-level-04',
            'rounded-sm border border-emphasis-subtle',
            'shadow-xl drop-shadow-xl',
          )}
        >
          {children}
        </div>
      }
      {...menuOptions}
      stopPropagation
      triggerAsChild
      onOpenAutoFocus={onOpenAutoFocus}
    >
      <IconButton
        aria-label="open options menu"
        iconType="ellipsis-vertical"
        size={size}
        iconVariant={iconVariant}
        disabled={disabled}
      />
    </Popover>
  );
}

KebabMenuComponent.displayName = 'KebabMenu';

export const KebabMenu = Object.assign(KebabMenuComponent, {
  Item: KebabMenuItem,
});
